import React, { useEffect, useRef, useState } from "react";
import handleApi from "../api/api";

const TRANSCRIPT_EPSILON = 0.001;

const formatSpeakerName = (speakerLabel, ...names) => {
  const userName = names.find((name) => typeof name === "string" && name.trim());
  if (userName) return userName.trim();

  // Preserve the original player labels when older JSON has no participant names.
  const label = String(speakerLabel || "").trim();
  const match = label.match(/(\d+)/);
  if (match) return `User ${match[1]}`;
  return label ? label.replace(/[_-]+/g, " ") : "Speaker";
};

const formatTimestamp = (rawSeconds) => {
  const totalSeconds = Math.max(0, Math.floor(Number(rawSeconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(
      seconds
    ).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const buildTranscriptRows = (transcriptData) => {
  const segments = transcriptData?.results?.speaker_labels?.segments ?? [];
  const items = transcriptData?.results?.items ?? [];

  if (!segments.length || !items.length) {
    return [];
  }

  const metadataBySpeaker = new Map();
  const addMetadata = (entry, speakerLabel = entry?.speaker_label) => {
    if (!speakerLabel) return;
    const metadata = metadataBySpeaker.get(speakerLabel) ?? {};
    if (!metadata.userName && typeof entry?.userName === "string") {
      metadata.userName = entry.userName.trim();
    }
    if (metadata.isClient === undefined && typeof entry?.isClient === "boolean") {
      metadata.isClient = entry.isClient;
    }
    metadataBySpeaker.set(speakerLabel, metadata);
  };
  segments.forEach((segment) => {
    addMetadata(segment);
    segment?.items?.forEach((item) =>
      addMetadata(item, item?.speaker_label || segment?.speaker_label)
    );
  });
  items.forEach((item) => addMetadata(item));
  const hasParticipantRoles = [...metadataBySpeaker.values()].some(
    (metadata) => typeof metadata.isClient === "boolean"
  );
  // Role-free legacy transcripts use user1/spk_0, or the first speaker, on the left.
  const legacyClientLabel = metadataBySpeaker.has("user1")
    ? "user1"
    : metadataBySpeaker.has("spk_0")
    ? "spk_0"
    : segments[0]?.speaker_label;
  const isClientSpeaker = (speakerLabel) =>
    hasParticipantRoles
      ? metadataBySpeaker.get(speakerLabel)?.isClient === true
      : speakerLabel === legacyClientLabel;
  const pronunciationItems = items.filter(
    (item) => item?.type === "pronunciation"
  );
  const hasSpeakerLabels =
    pronunciationItems.length > 0 &&
    pronunciationItems.every(
      (item) =>
        typeof item?.speaker_label === "string" && item.speaker_label.trim()
    );

  if (hasSpeakerLabels) {
    // Each participant can speak at the same time. Keep words and punctuation
    // with their own speaker instead of advancing one shared item cursor.
    const wordsBySpeaker = new Map();
    let previousSpeakerLabel = null;
    items.forEach((item) => {
      const speakerLabel =
        item?.speaker_label ||
        (item?.type === "punctuation" ? previousSpeakerLabel : null);
      if (!speakerLabel) return;
      if (!wordsBySpeaker.has(speakerLabel)) {
        wordsBySpeaker.set(speakerLabel, []);
      }
      const words = wordsBySpeaker.get(speakerLabel);
      const content = item?.alternatives?.[0]?.content ?? "";
      if (item?.type === "pronunciation") {
        previousSpeakerLabel = speakerLabel;
        words.push({ item, text: content });
      } else if (item?.type === "punctuation" && words.length) {
        words[words.length - 1].text += content;
      }
    });

    return segments
      .map((segment, segmentIndex) => {
        const speakerLabel = segment?.speaker_label || "speaker";
        const segmentItems = segment?.items ?? [];
        const segmentStart = Number(
          segmentItems[0]?.start_time ?? segment?.start_time ?? 0
        );
        const segmentEnd = Number(
          segmentItems[segmentItems.length - 1]?.end_time ??
            segment?.end_time ??
            segmentStart
        );
        const wordKey = (item) =>
          `${Number(item?.start_time)}:${Number(item?.end_time)}`;
        const segmentWordKeys = new Set(
          segmentItems
            .filter(
              (item) =>
                Number.isFinite(Number(item?.start_time)) &&
                Number.isFinite(Number(item?.end_time))
            )
            .map(wordKey)
        );
        const words = (wordsBySpeaker.get(speakerLabel) ?? []).filter(
          ({ item }) =>
            segmentWordKeys.size
              ? segmentWordKeys.has(wordKey(item))
              : Number(item.start_time) + TRANSCRIPT_EPSILON >= segmentStart &&
                Number(item.end_time) <= segmentEnd + TRANSCRIPT_EPSILON
        );
        if (!words.length) return null;
        return {
          id: `${speakerLabel}-${segmentStart}-${segmentIndex}`,
          speaker: formatSpeakerName(
            speakerLabel,
            segment?.userName,
            ...segmentItems.map((item) => item?.userName),
            ...words.map(({ item }) => item?.userName),
            metadataBySpeaker.get(speakerLabel)?.userName
          ),
          isClient: isClientSpeaker(speakerLabel),
          speakerId: String(speakerLabel)
            .toLowerCase()
            .replace(/[^a-z0-9_-]+/g, "-"),
          startTime: segmentStart,
          endTime: Number.isFinite(segmentEnd) ? segmentEnd : segmentStart,
          text: words
            .map(({ text }) => text)
            .join(" ")
            .replace(/\s+/g, " ")
            .trim(),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.startTime - b.startTime);
  }

  let itemIndex = 0;

  return segments
    .map((segment, segmentIndex) => {
      const speakerLabel = segment?.speaker_label || "speaker";
      const segmentItems = segment?.items ?? [];
      const firstSegmentItem = segmentItems[0];
      const lastSegmentItem = segmentItems[segmentItems.length - 1];
      const nextSegment = segments[segmentIndex + 1];
      const segmentStart = Number(
        firstSegmentItem?.start_time ?? segment?.start_time ?? 0
      );
      const nextSegmentStart = Number(
        nextSegment?.items?.[0]?.start_time ??
          nextSegment?.start_time ??
          Number.POSITIVE_INFINITY
      );

      while (itemIndex < items.length) {
        const candidate = items[itemIndex];
        const candidateStart = Number(candidate?.start_time);

        if (candidate?.type !== "pronunciation") {
          itemIndex += 1;
          continue;
        }

        if (
          Number.isFinite(candidateStart) &&
          candidateStart + TRANSCRIPT_EPSILON < segmentStart
        ) {
          itemIndex += 1;
          continue;
        }

        break;
      }

      const words = [];
      let localIndex = itemIndex;
      let lastEndTime = Number(
        lastSegmentItem?.end_time ?? segment?.end_time ?? segmentStart
      );

      while (localIndex < items.length) {
        const currentItem = items[localIndex];
        const currentType = currentItem?.type;
        const content = currentItem?.alternatives?.[0]?.content ?? "";
        const currentStart = Number(currentItem?.start_time);
        const currentEnd = Number(currentItem?.end_time);

        if (
          currentType === "pronunciation" &&
          Number.isFinite(currentStart) &&
          currentStart + TRANSCRIPT_EPSILON >= nextSegmentStart
        ) {
          break;
        }

        if (currentType === "pronunciation") {
          words.push(content);

          if (Number.isFinite(currentEnd)) {
            lastEndTime = currentEnd;
          }
        } else if (currentType === "punctuation" && words.length) {
          words[words.length - 1] = `${words[words.length - 1]}${content}`;
        }

        localIndex += 1;
      }

      if (!words.length) {
        return null;
      }

      itemIndex = localIndex;

      return {
        id: `${speakerLabel}-${segmentStart}-${segmentIndex}`,
        speaker: formatSpeakerName(
          speakerLabel,
          segment?.userName,
          ...segmentItems.map((item) => item?.userName),
          metadataBySpeaker.get(speakerLabel)?.userName
        ),
        isClient: isClientSpeaker(speakerLabel),
        speakerId: String(speakerLabel || "speaker")
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, "-"),
        startTime: segmentStart,
        endTime: Number.isFinite(lastEndTime) ? lastEndTime : segmentStart,
        text: words.join(" ").replace(/\s+/g, " ").trim(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.startTime - b.startTime);
};

const PlayerV2 = () => {
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transcriptAvailable, setTranscriptAvailable] = useState(false);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState(null);
  const [transcriptRows, setTranscriptRows] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get("cid");

    if (!cid) {
      setError("Missing cid in URL");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadTranscript = async (transcribeUrl) => {
      setTranscriptLoading(true);
      setTranscriptError(null);

      try {
        const response = await fetch(transcribeUrl);

        if (!response.ok) {
          throw new Error("Failed to load transcription");
        }

        const transcriptData = await response.json();

        if (cancelled) return;

        setTranscriptRows(buildTranscriptRows(transcriptData));
      } catch (e) {
        if (!cancelled) {
          setTranscriptRows([]);
          setTranscriptError(
            e?.message || "Transcription is available but could not be loaded"
          );
        }
      } finally {
        if (!cancelled) {
          setTranscriptLoading(false);
        }
      }
    };

    (async () => {
      try {
        const data = await handleApi("/get-v2-recording", "POST", {
          callerId: cid,
        });
        if (cancelled) return;
        const recordingData = data?.data ?? data;
        const url = recordingData?.url;
        const transcribeUrl = recordingData?.transcribe?.url;

        if (!url) {
          setError("Recording URL not available");
          setLoading(false);
        } else {
          setVideoUrl(url);
          setError(null);
          setLoading(false);
        }

        setTranscriptAvailable(Boolean(transcribeUrl));
        setTranscriptRows([]);
        setTranscriptError(null);
        setTranscriptLoading(false);

        if (transcribeUrl) {
          loadTranscript(transcribeUrl);
        }
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            "Failed to load recording";
          setError(msg);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const seekToTranscriptTime = (seconds) => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.currentTime = Number(seconds) || 0;
    setCurrentTime(Number(seconds) || 0);
  };

  if (loading) {
    return (
      <div className="wrapper">
        <p>Loading recording…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wrapper">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="wrapper player-v2-wrapper">
      <div className="video-container">
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          playsInline
          onTimeUpdate={(event) => {
            setCurrentTime(event.currentTarget.currentTime);
          }}
        />
      </div>

      {transcriptAvailable ? (
        <section className="transcript-panel">
          <div className="transcript-header">
            <div>
              <h2>Conversation Transcript</h2>
              <p>Speaker turns with timestamps</p>
            </div>
          </div>

          {transcriptLoading ? (
            <p className="transcript-status">Loading transcription...</p>
          ) : transcriptError ? (
            <p className="transcript-status transcript-status-error">
              {transcriptError}
            </p>
          ) : transcriptRows.length ? (
            <div className="transcript-list">
              {transcriptRows.map((row) => {
                const isActive =
                  currentTime >= row.startTime && currentTime <= row.endTime;

                return (
                  <article
                    key={row.id}
                    className={`transcript-row ${row.speakerId} ${
                      row.isClient ? "transcript-client" : "transcript-participant"
                    } ${
                      isActive ? "active" : ""
                    }`}
                  >
                    <div className="transcript-row-meta">
                      <span className="transcript-speaker">{row.speaker}</span>
                      <button
                        type="button"
                        className="transcript-time"
                        onClick={() => seekToTranscriptTime(row.startTime)}
                      >
                        {formatTimestamp(row.startTime)}
                      </button>
                    </div>
                    <p className="transcript-text">{row.text}</p>
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="transcript-status">
              Transcription is available, but no speaker turns were found.
            </p>
          )}
        </section>
      ) : null}
    </div>
  );
};

export default PlayerV2;
